import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type { WhatsappValidationResult, WhatsappValidationSeverity } from '@/shared/types/whatsapp';

const SEVERITY_RANK: Record<WhatsappValidationSeverity, number> = {
  blocked: 3,
  limited: 2,
  action_required: 1,
};

const SEVERITY_VARIANT: Record<WhatsappValidationSeverity, 'destructive' | 'warning'> = {
  blocked: 'destructive',
  limited: 'warning',
  action_required: 'warning',
};

const SEVERITY_TITLE_KEY: Record<WhatsappValidationSeverity, string> = {
  blocked: 'integrations.whatsapp.health_status.blocked.title',
  limited: 'integrations.whatsapp.health_status.limited.title',
  action_required: 'integrations.con_status.action_required',
};

const BLOCKING_TITLE_KEY = 'integrations.whatsapp.validation.blocking.title';

const pickOverallSeverity = (issues: WhatsappValidationResult[]): WhatsappValidationSeverity =>
  issues.reduce<WhatsappValidationSeverity>(
    (acc, issue) =>
      issue.severity && SEVERITY_RANK[issue.severity] > SEVERITY_RANK[acc] ? issue.severity : acc,
    'action_required'
  );

interface Props {
  blocking: boolean;
  issues: WhatsappValidationResult[];
  renderIssue: (issue: WhatsappValidationResult) => ReactNode;
}

export const ValidationGroup = ({ blocking, issues, renderIssue }: Props) => {
  const { t } = useTranslation();
  const severity = pickOverallSeverity(issues);
  const titleKey = blocking ? BLOCKING_TITLE_KEY : SEVERITY_TITLE_KEY[severity];

  return (
    <Alert variant={SEVERITY_VARIANT[severity]} className="mb-4">
      <AlertDescription>
        <p className="font-medium">{t(titleKey)}</p>
        <ul className="mt-2 space-y-3">{issues.map(renderIssue)}</ul>
      </AlertDescription>
    </Alert>
  );
};
